import { ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import {
  IQuestionnaireChoice,
  IQuestionnaireQuestion,
} from 'src/app/interfaces/Questionnaire/IQuestionnaire';
import { EpisodeOfCareService } from 'src/app/services/episodeOfCare.service';
import { PermissionService } from 'src/app/services/permission.service';

@Component({
  selector: 'app-sldc-questions',
  templateUrl: './sldc-questions.component.html',
  styleUrl: './sldc-questions.component.css',
  standalone: false,
})
export class SldcQuestionsComponent implements OnInit {
  @Input() elements: IQuestionnaireQuestion[] = [];
  @Input() formGroup!: FormGroup;
  @Input() readonlyMode = false;
  private sub?: Subscription;

  constructor(
    private readonly eocService: EpisodeOfCareService,
    private readonly router: Router,
    private readonly cdr: ChangeDetectorRef,
    private readonly translate: TranslateService,
    private readonly permissionService: PermissionService,
  ) {}

  ngOnInit(): void {
    const totalCtrl = this.totalControlName
      ? this.formGroup.get(this.totalControlName)
      : null;
    if (totalCtrl) {
      totalCtrl.disable({ emitEvent: false });
    }

    this.sub = this.formGroup.valueChanges.subscribe(() => {
      this.recalculateTotal();
    });

    this.recalculateTotal();
  }

  isInpatientView(): boolean {
    return (
      !(
        this.eocService.currentEocId() === null ||
        this.eocService.currentEocId() === ''
      ) && this.router.url.includes('inPatientView')
    );
  }

  isEOC() {
    return (
      !(
        this.eocService.currentEocId() === null ||
        this.eocService.currentEocId() === ''
      ) && this.router.url.includes('episodeOfCare')
    );
  }

  hasPermission(permission: string[]): boolean {
    return this.permissionService.can(permission);
  }

  setNotMentioned(fieldName: string): void {
    this.formGroup
      .get(fieldName)
      ?.setValue(this.translate.instant('Not mentioned'));
    this.cdr.detectChanges();
  }

  onRadioClick(controlName: string, value: any): void {
    if (this.readonlyMode) return;

    const control = this.formGroup.get(controlName);
    if (!control) return;

    if (control.value === value) {
      control.setValue(null);
    }
  }

  getQuestionTitle(title?: string): string {
    return title?.split('||')[0]?.trim() ?? '';
  }

  getQuestionInstruction(title?: string): string {
    return title?.split('||')[1]?.trim() ?? '';
  }

  private recalculateTotal(): void {
    const totalCtrl = this.totalControlName
      ? this.formGroup.get(this.totalControlName)
      : null;
    if (!totalCtrl) return;

    let total = 0;

    for (const el of this.elements ?? []) {
      if (!el.name) continue;
      if (el.name === this.totalControlName) continue;

      const selectedValue = this.formGroup.get(el.name)?.value;
      if (selectedValue == null || selectedValue === '') continue;

      const choice = (el.choices ?? []).find(
        (c: IQuestionnaireChoice) => c.value === selectedValue,
      );

      const selectedText = (choice?.text ?? '').trim();
      total += this.scoreSldcAnswer(selectedText);
    }

    totalCtrl.setValue(String(total), { emitEvent: false });
    totalCtrl.disable({ emitEvent: false });
    this.cdr.detectChanges();
  }

  private scoreSldcAnswer(selectedText: string): number {
    if (!selectedText) return 0;

    const match = selectedText.match(/\b([0-4])\b/);
    return match ? Number(match[1]) : 0;
  }

  get totalScore(): number {
    let total = 0;

    for (const el of this.elements ?? []) {
      if (!el.name || el.type !== 'radiogroup') continue;

      const selectedValue = this.formGroup.get(el.name)?.value;
      if (selectedValue == null || selectedValue === '') continue;

      const choice = (el.choices ?? []).find(
        (c: IQuestionnaireChoice) => c.value === selectedValue,
      );

      total += this.scoreSldcAnswer((choice?.text ?? '').trim());
    }

    return total;
  }

  private get totalControlName(): string | undefined {
    return this.elements.find((el) => el.type !== 'radiogroup' && el.name)
      ?.name;
  }
}
